'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import Image, { StaticImageData } from 'next/image';
import Foldbefore from '@/imgs/Foldbefore.jpg';
import Foldafter from '@/imgs/Foldafter.jpg';
import Ironbefore from '@/imgs/Ironbefore.jpg';
import Ironafter from '@/imgs/Ironafter.jpg';
import { useRouter } from 'next/navigation';
import HeroSection from './HeroSection';
import Testimonials from './Testimonials';
import CTASection from './CTASection';
import Footer from './Footer';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { getStorageUrl } from '@/lib/api';
import type { AgentMarkerData } from '@/types/agent';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api';
const DEFAULT_AGENT_IMAGE = '/assets/PlaceHolder.png';

interface Area {
  id: string;
  name: string;
  agentCount: number;
}

interface Testimonial {
  id: string | number;
  name: string;
  role?: string;
  area: string;
  image?: string;
  alt?: string;
  rating: number;
  comment: string;
  date: string;
}

interface ApiTestimonial {
  id: number;
  customer_name: string;
  customer_area: string | null;
  customer_image: string | null;
  rating: number;
  content: string;
  is_featured: boolean;
  created_at: string;
}

interface ServiceImage {
  id: number;
  url: string;
}

interface ServicePricingItem {
  id: number;
  categoryGroup: string;
  categoryName: string;
  unitPrice: number;
}

interface Service {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  turnaround: string | null;
  displayPrice: number | null;
  priceUnit: string | null;
  beforeImages: ServiceImage[];
  afterImages: ServiceImage[];
  pricingItems: ServicePricingItem[];
}

interface BeforeAfterSliderProps {
  beforeSrc: string | StaticImageData;
  afterSrc: string | StaticImageData;
  alt: string;
}

/**
 * Convert lat/lng coordinates to percentage positions on the Malaysia SVG map.
 * Mapping derived from actual SVG path positions (viewBox 0 0 1000 332):
 *
 * Peninsular Malaysia reference points:
 * - Perlis (lat 6.5, lng 100.2) → SVG x=79 (8%), y=54 (16%)
 * - Johor (lat 1.5, lng 103.8) → SVG x=182 (18%), y=245 (74%)
 *
 * East Malaysia reference points:
 * - Sarawak (lat 2.5, lng 113) → SVG x=776 (78%), y=127 (38%)
 * - Sabah (lat 5.5, lng 117) → SVG x=891 (89%), y=164 (49%)
 */
function convertToMapPosition(
  agentLat: number | null,
  agentLng: number | null
): { x: number; y: number } {
  if (agentLat === null || agentLng === null) {
    // Return center of Peninsular Malaysia (KL area) if no coordinates
    return { x: 12, y: 55 };
  }

  let x: number;
  let y: number;

  // Determine region based on longitude
  if (agentLng < 108) {
    // Peninsular Malaysia
    // Longitude mapping: 100.2 → 8%, 103.8 → 18%
    const lngRange = 103.8 - 100.2; // 3.6 degrees
    const xRange = 18 - 8; // 10% of SVG width
    x = 8 + ((agentLng - 100.2) / lngRange) * xRange;

    // Latitude mapping: 6.5 → 16%, 1.5 → 74% (inverted)
    const latRange = 6.5 - 1.5; // 5 degrees
    const yRange = 74 - 16; // 58% of SVG height
    y = 16 + ((6.5 - agentLat) / latRange) * yRange;
  } else {
    // East Malaysia (Sabah/Sarawak)
    // Longitude mapping: 109.5 → 50%, 119 → 95%
    const lngRange = 119 - 109.5; // 9.5 degrees
    const xRange = 95 - 50; // 45% of SVG width
    x = 50 + ((agentLng - 109.5) / lngRange) * xRange;

    // Latitude mapping: 7 → 25%, 1 → 70% (inverted)
    const latRange = 7 - 1; // 6 degrees
    const yRange = 70 - 25; // 45% of SVG height
    y = 25 + ((7 - agentLat) / latRange) * yRange;
  }

  // Clamp to safe bounds
  return {
    x: Math.max(5, Math.min(95, x)),
    y: Math.max(10, Math.min(80, y)),
  };
}

/**
 * Generate a tag for the agent based on their ranking and area.
 */
function generateAgentTag(agent: { area: string; rating: number; reviewCount: number }, index: number): string {
  if (index === 0) return `Top Rated in ${agent.area.split(',')[0]}`;
  if (agent.rating >= 4.9) return `Premium in ${agent.area.split(',')[0]}`;
  if (agent.reviewCount > 100) return `Popular in ${agent.area.split(',')[0]}`;
  return `Trusted in ${agent.area.split(',')[0]}`;
}

function BeforeAfterSlider({ beforeSrc, afterSrc, alt }: BeforeAfterSliderProps) {
  const [position, setPosition] = useState<number>(50);
  const snapTo = (p: number) => setPosition(Math.min(Math.max(p, 0), 100));

  return (
    <div className="relative h-64 select-none">
      <div className="absolute inset-0">
        <Image src={beforeSrc} alt={`${alt} (Sebelum)`} fill className="object-cover" />
      </div>

      <div
        className="absolute inset-y-0 left-0 overflow-hidden transition-all duration-300 ease-in-out"
        style={{ width: `${position}%` }}
      >
        <div className="relative h-full">
          <Image src={afterSrc} alt={`${alt} (Selepas)`} fill className="object-cover" />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

      {/* Divider and drag handle removed; buttons control the slider */}

      <div className="absolute bottom-6 left-6 flex gap-2">
        <button
          onClick={() => snapTo(0)}
          className="bg-black/60 backdrop-blur-md px-4 py-1.5 rounded-lg text-white text-[11px] font-cta font-black uppercase tracking-wider"
          aria-label="Tunjuk Sebelum"
        >
          SEBELUM
        </button>
        <button
          onClick={() => snapTo(100)}
          className="bg-accent/90 backdrop-blur-md px-4 py-1.5 rounded-lg text-secondary text-[11px] font-cta font-black uppercase tracking-wider"
          aria-label="Tunjuk Selepas"
        >
          SELEPAS
        </button>
      </div>
    </div>
  );
}

const HomepageInteractive = () => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);

  // Agent state
  const [agentMarkers, setAgentMarkers] = useState<AgentMarkerData[]>([]);
  const [totalAgents, setTotalAgents] = useState(0);
  const [isLoadingAgents, setIsLoadingAgents] = useState(true);
  const [agentError, setAgentError] = useState<string | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Fetch services on mount
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${API_URL}/services`);
        if (response.ok) {
          const data = await response.json();
          setServices(data.services);
        }
      } catch (err) {
        console.error('Failed to fetch services:', err);
      }
    };

    fetchServices();
  }, []);

  // Fetch testimonials on mount
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch(`${API_URL}/testimonials?featured=true&limit=10`);
        if (response.ok) {
          const data = await response.json();
          // Transform API response to match Testimonial interface
          const transformedTestimonials: Testimonial[] = data.testimonials.map((t: ApiTestimonial) => ({
            id: t.id,
            name: t.customer_name,
            area: t.customer_area || '',
            image: t.customer_image || undefined,
            rating: t.rating,
            comment: t.content,
            date: t.created_at,
          }));
          setTestimonials(transformedTestimonials);
        }
      } catch (err) {
        console.error('Failed to fetch testimonials:', err);
      }
    };

    fetchTestimonials();
  }, []);

  // Fetch all agents on mount
  useEffect(() => {
    const fetchAllAgents = async () => {
      setIsLoadingAgents(true);
      setAgentError(null);

      try {
        const response = await fetch(`${API_URL}/agents/for-map`);
        if (!response.ok) throw new Error('Failed to fetch agents');

        const data = await response.json();

        // Convert agents to marker data with x/y positions
        const markers: AgentMarkerData[] = data.agents.map((agent: any, index: number) => {
          const { x, y } = convertToMapPosition(
            agent.coordinates.lat,
            agent.coordinates.lng
          );

          return {
            id: agent.id,
            slug: agent.slug,
            name: agent.name,
            area: agent.area,
            image: getStorageUrl(agent.image) || agent.image,
            alt: `${agent.name} - Ejen di ${agent.area}`,
            rating: agent.rating,
            reviewCount: agent.reviewCount,
            completedOrders: agent.completedOrders,
            phone: agent.phone,
            services: agent.services,
            x,
            y,
            tag: generateAgentTag(agent, index),
          };
        });

        setAgentMarkers(markers);
        setTotalAgents(data.total);
      } catch (err) {
        console.error('Failed to fetch agents:', err);
        setAgentError('Gagal memuatkan ejen. Sila cuba semula.');
      } finally {
        setIsLoadingAgents(false);
      }
    };

    fetchAllAgents();
  }, []);

  const mockAreas: Area[] = [
    { id: 'shah-alam', name: 'Shah Alam', agentCount: 45 },
    { id: 'petaling-jaya', name: 'Petaling Jaya', agentCount: 38 },
    { id: 'subang-jaya', name: 'Subang Jaya', agentCount: 32 },
    { id: 'klang', name: 'Klang', agentCount: 28 },
    { id: 'puchong', name: 'Puchong', agentCount: 35 },
    { id: 'cyberjaya', name: 'Cyberjaya', agentCount: 22 },
    { id: 'kajang', name: 'Kajang', agentCount: 26 },
    { id: 'ampang', name: 'Ampang', agentCount: 30 },
    { id: 'cheras', name: 'Cheras', agentCount: 40 },
    { id: 'kepong', name: 'Kepong', agentCount: 25 },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // SVG Map refs and state
  const mapObjRef = useRef<HTMLObjectElement | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapUrl = '/assets/malaysia-map.svg';

  const [hoveredState, setHoveredState] = useState<{
    name: string;
    count: number;
    x: number;
    y: number;
  } | null>(null);

  // Count agents by state for map tooltip
  // Extract state from area string (e.g., "Sepang, Selangor" → "Selangor")
  const agentsByState = useMemo(() => {
    const counts: Record<string, number> = {};
    agentMarkers.forEach((a) => {
      const parts = a.area.split(',');
      // State is typically the last part (e.g., "Sepang, Selangor" or "Kuala Lumpur")
      const state = parts.length > 1 ? parts[parts.length - 1].trim() : parts[0].trim();
      if (state) counts[state] = (counts[state] || 0) + 1;
    });
    return counts;
  }, [agentMarkers]);

  // Ref to hold current agentsByState for event handlers (avoids stale closure)
  const agentsByStateRef = useRef<Record<string, number>>({});
  useEffect(() => {
    agentsByStateRef.current = agentsByState;
  }, [agentsByState]);

  // Computed stats
  const activeAgentsCount = agentMarkers.length;
  const completedOrdersCount = agentMarkers.reduce((sum, a) => sum + (a.completedOrders || 0), 0);

  // Handle SVG map load and attach interactive events
  const handleMapLoad = () => {
    const obj = mapObjRef.current;
    const containerRect = mapContainerRef.current?.getBoundingClientRect();
    if (!obj || !obj.contentDocument || !containerRect) return;
    const doc = obj.contentDocument;
    const paths = Array.from(doc.querySelectorAll('path[name]')) as SVGPathElement[];
    paths.forEach((p) => {
      p.style.cursor = 'pointer';
      p.addEventListener('mouseenter', (e: MouseEvent) => {
        const name = p.getAttribute('name') || '';
        const count = agentsByStateRef.current[name] || 0;
        const originalStroke = p.style.stroke || '';
        const originalStrokeWidth = p.style.strokeWidth || '';
        const originalFill = p.style.fill || '';
        (p as any).__originalStroke = originalStroke;
        (p as any).__originalStrokeWidth = originalStrokeWidth;
        (p as any).__originalFill = originalFill;
        p.style.stroke = '#4c1d95';
        p.style.strokeWidth = '1.5';
        p.style.fill = '#4c1d95';
        p.style.filter = 'drop-shadow(0 0 14px rgba(76,29,149,0.55))';
        const x = e.clientX - containerRect.left;
        const y = e.clientY - containerRect.top;
        setHoveredState({ name, count, x, y });
      });
      p.addEventListener('mousemove', (e: MouseEvent) => {
        const containerRect2 = mapContainerRef.current?.getBoundingClientRect();
        if (!containerRect2) return;
        const x = e.clientX - containerRect2.left;
        const y = e.clientY - containerRect2.top;
        setHoveredState((prev) => (prev ? { ...prev, x, y } : prev));
      });
      p.addEventListener('mouseleave', () => {
        const originalStroke = (p as any).__originalStroke as string;
        const originalStrokeWidth = (p as any).__originalStrokeWidth as string;
        const originalFill = (p as any).__originalFill as string;
        p.style.stroke = originalStroke;
        p.style.strokeWidth = originalStrokeWidth;
        p.style.fill = originalFill;
        p.style.filter = '';
        setHoveredState(null);
      });
      p.addEventListener('click', () => {
        const name = p.getAttribute('name') || '';
        if (name) {
          router.push(`/agent-directory?area=${encodeURIComponent(name)}`);
        }
      });
    });
  };

  useEffect(() => {
    if (!isPaused && agentMarkers.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % agentMarkers.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isPaused, agentMarkers.length]);

  const selectedAgent = agentMarkers[currentIndex];

  // Fallback testimonials when API returns empty
  const fallbackTestimonials: Testimonial[] = [
    {
      id: 'test-001',
      name: 'Encik Ahmad Razali',
      area: 'Shah Alam',
      rating: 5,
      comment:
        'Servis yang sangat memuaskan! Puan Siti sangat teliti dan pakaian saya sentiasa kemas dan wangi. Harga pun berpatutan. Saya sudah 6 bulan menggunakan servisnya dan tidak pernah kecewa.',
      date: '15 Disember 2025',
    },
    {
      id: 'test-002',
      name: 'Puan Mariam Abdullah',
      area: 'Petaling Jaya',
      rating: 5,
      comment:
        'Sebagai ibu bekerja, servis ini sangat membantu saya. Puan Fatimah sangat fleksibel dengan masa dan pakaian anak-anak saya sentiasa dilipat dengan kemas. Highly recommended!',
      date: '28 November 2025',
    },
    {
      id: 'test-003',
      name: 'Encik Hafiz Ismail',
      area: 'Subang Jaya',
      rating: 5,
      comment:
        'Puan Aminah sangat profesional dan amanah. Baju kerja saya sentiasa dalam keadaan sempurna. Komunikasi melalui WhatsApp juga sangat mudah dan cepat.',
      date: '10 Disember 2025',
    },
    {
      id: 'test-004',
      name: 'Puan Nurul Huda',
      area: 'Klang',
      rating: 4,
      comment:
        'Servis yang baik dan harga berpatutan. Puan Zainab sangat mesra dan sentiasa memastikan pakaian dalam keadaan terbaik sebelum pulangkan.',
      date: '5 Januari 2026',
    },
  ];

  // Use fetched testimonials or fallback
  const displayTestimonials = testimonials.length > 0 ? testimonials : fallbackTestimonials;

  const handleAreaSelect = (areaId: string, locationType?: string) => {
    if (isHydrated) {
      const params = new URLSearchParams();
      params.set('area', areaId);
      if (locationType) {
        params.set('type', locationType);
      }
      router.push(`/agent-directory?${params.toString()}`);
    }
  };

  // Find lipat and gosok services from fetched data
  const lipatService = services.find(s => s.slug === 'lipat-baju' || s.name.toLowerCase().includes('lipat'));
  const gosokService = services.find(s => s.slug === 'gosok-baju' || s.name.toLowerCase().includes('gosok'));

  // Get display price and images with fallbacks
  const lipatPrice = lipatService?.displayPrice ? `RM${lipatService.displayPrice.toFixed(2)}` : 'RM25.00';
  const lipatBeforeImg = lipatService?.beforeImages[0]?.url || Foldbefore;
  const lipatAfterImg = lipatService?.afterImages[0]?.url || Foldafter;

  const gosokPrice = 'RM1.50'; // Hardcoded price
  const gosokBeforeImg = gosokService?.beforeImages[0]?.url || Ironbefore;
  const gosokAfterImg = gosokService?.afterImages[0]?.url || Ironafter;

  return (
    <div className="min-h-screen bg-background">
      <HeroSection areas={mockAreas} onAreaSelect={handleAreaSelect} />

      <section id="services" className="py-32 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-20">
            <div className="w-full md:w-1/2">
              <div className="rounded-3xl overflow-hidden shadow-2xl relative border-4 border-white">
                <BeforeAfterSlider
                  beforeSrc={lipatBeforeImg}
                  afterSrc={lipatAfterImg}
                  alt="Lipat baju sebelum dan selepas"
                />
              </div>
            </div>
            <div className="w-full md:w-1/2 space-y-8">
              <div className="space-y-6">
                <h3 className="text-5xl font-heading font-black text-primary leading-tight">
                  SERVIS LIPAT BAJU <br />
                  <span className="text-accent">(PER BAKUL)</span>
                </h3>
                <p className="text-textSecondary text-lg leading-relaxed font-body">
                  Uruskan timbunan baju anda dengan lebih teratur dan kemas. Setiap bakul dilipat
                  mengikut kategori dan dibungkus rapi.
                </p>
                <div className="space-y-3">
                  <h4 className="text-primary font-cta font-black text-lg">Harga Melipat Baju</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {(lipatService?.pricingItems && lipatService.pricingItems.length > 0
                      ? lipatService.pricingItems
                      : [
                          { id: 1, categoryGroup: 'kecil', categoryName: 'Bakul Kecil', unitPrice: 10 },
                          { id: 2, categoryGroup: 'sederhana', categoryName: 'Bakul Sederhana', unitPrice: 20 },
                          { id: 3, categoryGroup: 'besar', categoryName: 'Bakul Besar', unitPrice: 30 },
                        ]
                    ).map((item) => (
                      <div key={item.id} className="rounded-2xl border border-purple-100 bg-white px-6 py-4">
                        <div className="text-textSecondary font-body">{item.categoryName}</div>
                        <div className="text-2xl font-heading font-black text-primary">
                          RM{item.unitPrice % 1 === 0 ? item.unitPrice : item.unitPrice.toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-textSecondary font-body">
                    <Icon
                      name="CheckCircleIcon"
                      variant="solid"
                      size={18}
                      className="text-green-600"
                    />
                    <span>Tiada caj timbang</span>
                  </div>
                  <div className="flex items-center gap-2 text-textSecondary font-body">
                    <Icon
                      name="CheckCircleIcon"
                      variant="solid"
                      size={18}
                      className="text-green-600"
                    />
                    <span>Campur pelbagai jenis pakaian</span>
                  </div>
                  <div className="flex items-center gap-2 text-textSecondary font-body">
                    <Icon
                      name="CheckCircleIcon"
                      variant="solid"
                      size={18}
                      className="text-green-600"
                    />
                    <span>Harga dipersetujui sebelum servis bermula</span>
                  </div>
                </div>
              </div>
              <a
                href="/agent-directory"
                className="inline-flex items-center gap-3 bg-accent text-secondary px-10 py-5 rounded-2xl font-cta font-black text-lg hover:bg-primary hover:text-accent transition-all shadow-xl"
              >
                Pilih Ejen Melipat
                <Icon name="ArrowRightIcon" size={18} variant="outline" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 bg-purple-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col-reverse md:flex-row items-center gap-20">
            <div className="w-full md:w-1/2 space-y-8">
              <div className="space-y-6">
                <h3 className="text-5xl font-heading font-black text-primary leading-tight">
                  SERVIS GOSOK BAJU <br />
                  <span className="text-accent">(PER HELAI)</span>
                </h3>
                <p className="text-textSecondary text-lg leading-relaxed font-body">
                  Nikmati pakaian yang kemas, licin dan sedia dipakai.
                </p>
                <p className="text-textSecondary text-lg leading-relaxed font-body">
                  Sesuai untuk pakaian harian, kerja dan majlis khas.
                </p>
                <div className="space-y-3">
                  <h4 className="text-primary font-cta font-black text-lg">Harga Menggosok Baju</h4>
                  <p className="text-textSecondary font-body text-lg">Bermula dari RM1 / helai</p>
                </div>
                <div className="space-y-2">
                  <p className="text-textSecondary font-body">Caj tambahan untuk:</p>
                  <ul className="list-disc list-inside text-textSecondary font-body">
                    <li>Pakaian saiz besar (2XL ke atas)</li>
                  </ul>
                  <p className="text-textSecondary font-body">
                    Harga akhir akan dimaklumkan dan dipersetujui bersama ejen sebelum servis
                    bermula.
                  </p>
                </div>
              </div>
              <a
                href="/agent-directory"
                className="inline-flex items-center gap-3 bg-accent text-secondary px-10 py-5 rounded-2xl font-cta font-black text-lg hover:bg-primary hover:text-accent transition-all shadow-xl"
              >
                Pilih Ejen Menggosok
                <Icon name="ArrowRightIcon" size={18} variant="outline" />
              </a>
            </div>
            <div className="w-full md:w-1/2">
              <div className="rounded-3xl overflow-hidden shadow-2xl relative border-4 border-white">
                <BeforeAfterSlider
                  beforeSrc={gosokBeforeImg}
                  afterSrc={gosokAfterImg}
                  alt="Gosok baju sebelum dan selepas"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto space-y-8">
            <div className="text-center space-y-3">
              <h2 className="text-4xl font-heading font-black text-primary">CAJ TAMBAHAN</h2>
              <p className="text-textSecondary font-body text-lg">
                Beberapa caj tambahan mungkin dikenakan mengikut keperluan:
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  id: 'hanger',
                  icon: 'TagIcon',
                  title: 'Hanger',
                  price: 'RM0.20',
                  unit: '/ unit',
                  detail: 'Bawa sendiri percuma',
                },
                {
                  id: 'delivery',
                  icon: 'TruckIcon',
                  title: 'Delivery oleh ejen',
                  price: 'RM1',
                  unit: '/ km (sehala)',
                  detail: 'Kadar sehala berdasarkan jarak',
                },
                {
                  id: 'hq',
                  icon: 'ShieldCheckIcon',
                  title: 'Caj sistem HQ',
                  price: 'RM3',
                  unit: '/ order',
                  detail: 'Wajib untuk setiap tempahan',
                },
                {
                  id: 'highrise',
                  icon: 'BuildingOfficeIcon',
                  title: 'High-rise tanpa lift',
                  price: 'RM1',
                  unit: '/ tingkat',
                  detail: 'Flat/Apartment tanpa lif',
                },
                {
                  id: 'lift',
                  icon: 'ArrowUpOnSquareStackIcon',
                  title: 'Lift bermasalah',
                  price: 'RM1',
                  unit: '/ sekali naik',
                  detail: 'Bayaran setiap trip naik',
                },
              ].map((c) => (
                <div
                  key={c.id}
                  className="group rounded-2xl border border-purple-100 bg-white px-6 py-5 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className="flex items-baseline justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name={c.icon} size={16} variant="outline" className="text-primary" />
                      </div>
                      <div className="text-primary font-heading font-black">{c.title}</div>
                    </div>
                    <div className="text-2xl font-heading font-black text-primary">{c.price}</div>
                  </div>
                  <div className="text-textSecondary font-body text-sm">{c.unit}</div>
                  <div className="mt-3 text-[12px] font-body text-textSecondary">{c.detail}</div>
                </div>
              ))}
            </div>
            <p className="text-textSecondary font-body text-sm text-center">
              Semua caj akan dimaklumkan awal supaya tiada salah faham.
            </p>
          </div>
        </div>
      </section>

      <section id="map" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-4xl font-heading font-black text-primary mb-2">
                CARI EJEN BERDEKATAN ANDA
              </h2>
              <p className="text-textSecondary font-body text-lg">
                Semua ejen Surilipat adalah dari komuniti setempat.
              </p>
              <p className="text-textSecondary font-body text-lg">
                Mereka lebih memahami kawasan, fleksibel dari segi masa dan mudah dihubungi.
              </p>
            </div>
            <div className="flex items-center gap-2 bg-green-50 px-4 py-2 rounded-full border border-green-100">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-green-700 text-sm font-bold">
                {totalAgents > 0 ? totalAgents : activeAgentsCount} Ejen Aktif
              </span>
            </div>
            <a
              href="/agent-directory"
              className="hidden md:inline-flex px-6 py-3 bg-green-600 text-white rounded-xl font-cta font-bold hover:bg-green-700 transition-all"
            >
              Cari Ejen Berdekatan
            </a>
          </div>

          <div
            ref={mapContainerRef}
            className="relative bg-slate-100 rounded-[3rem] p-4 shadow-inner border-8 border-purple-50 overflow-hidden min-h-[400px]"
          >
            {/* SVG Map Background */}
            <AppImage
              src={mapUrl}
              alt="Peta Malaysia"
              fill
              className="absolute inset-0 opacity-30 pointer-events-none"
            />
            <object
              ref={mapObjRef}
              data={mapUrl}
              type="image/svg+xml"
              className="absolute inset-0 w-full h-full opacity-40 z-10"
              onLoad={handleMapLoad}
              aria-label="Peta Malaysia interaktif"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-slate-200/30 via-slate-100/30 to-slate-200/30 pointer-events-none" />

            {/* State hover tooltip */}
            {hoveredState && (
              <div
                className="absolute z-40 bg-white/95 backdrop-blur px-3 py-2 rounded-lg border border-primary/20 shadow-lg pointer-events-none"
                style={{
                  left: Math.min(hoveredState.x + 10, (mapContainerRef.current?.offsetWidth || 400) - 120),
                  top: hoveredState.y < 60 ? hoveredState.y + 20 : hoveredState.y - 50,
                }}
              >
                <div className="text-[11px] font-body text-textSecondary">{hoveredState.name}</div>
                <div className="text-sm font-heading font-black text-primary">
                  {hoveredState.count} ejen
                </div>
              </div>
            )}

            {/* Loading state */}
            {isLoadingAgents && (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-slate-100/80">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-textSecondary font-body">Memuat ejen...</p>
                </div>
              </div>
            )}

            {/* Error state */}
            {agentError && !isLoadingAgents && (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-slate-100/80">
                <div className="text-center">
                  <Icon name="ExclamationCircleIcon" size={48} variant="outline" className="text-red-400 mx-auto mb-4" />
                  <p className="text-textSecondary font-body mb-4">{agentError}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-accent text-secondary rounded-lg font-cta font-bold"
                  >
                    Cuba Semula
                  </button>
                </div>
              </div>
            )}

            {/* Empty state */}
            {!isLoadingAgents && !agentError && agentMarkers.length === 0 && (
              <div className="absolute inset-0 z-30 flex items-center justify-center bg-slate-100/80">
                <div className="text-center">
                  <Icon name="MapPinIcon" size={48} variant="outline" className="text-slate-400 mx-auto mb-4" />
                  <p className="text-textSecondary font-body mb-2">Tiada ejen didaftarkan lagi</p>
                  <a
                    href="/agent-registration"
                    className="text-accent font-cta font-bold hover:underline"
                  >
                    Daftar sebagai ejen
                  </a>
                </div>
              </div>
            )}

            {/* Agent markers on map */}
            <div className="absolute inset-0 z-20 pointer-events-none">
              {agentMarkers.map((agent, idx) => (
                <button
                  key={agent.id}
                  onClick={() => {
                    setCurrentIndex(idx);
                    setIsPaused(true);
                    setTimeout(() => setIsPaused(false), 10000);
                  }}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 transition-transform pointer-events-auto ${
                    idx === currentIndex ? 'scale-110 z-10' : 'scale-100'
                  }`}
                  style={{ left: `${agent.x}%`, top: `${agent.y}%` }}
                  aria-label={`Pilih ${agent.name} di ${agent.area}`}
                >
                  <div
                    className={`w-9 h-9 rounded-full border-2 shadow-xl flex items-center justify-center transition-all ${
                      idx === currentIndex
                        ? 'bg-accent border-white ring-2 ring-accent/30 animate-pulse'
                        : 'bg-primary border-white hover:scale-110'
                    }`}
                  >
                    <Icon
                      name={idx === currentIndex ? 'UserCircleIcon' : 'MapPinIcon'}
                      size={16}
                      variant="solid"
                      className="text-secondary"
                    />
                  </div>
                </button>
              ))}
            </div>

            {/* Selected agent card - bottom right */}
            {selectedAgent && (
              <div className="absolute bottom-6 right-6 z-30">
                <div className="bg-white p-4 rounded-2xl shadow-2xl border border-accent/30 w-[180px]">
                  {selectedAgent.tag && (
                    <div className="absolute -top-2 right-2 bg-accent text-secondary px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest whitespace-nowrap">
                      {selectedAgent.tag}
                    </div>
                  )}
                  <div className="flex flex-col items-center gap-2 mb-3 mt-1">
                    <div className="w-16 h-16 rounded-full overflow-hidden border border-accent/20">
                      <AppImage
                        src={selectedAgent.image || DEFAULT_AGENT_IMAGE}
                        alt={selectedAgent.alt}
                        className="w-full h-full object-cover"
                        fallbackSrc={DEFAULT_AGENT_IMAGE}
                      />
                    </div>
                    <p className="text-[11px] font-body text-textSecondary text-center">
                      {selectedAgent.area}
                    </p>
                    <p className="text-sm font-heading font-bold text-textPrimary text-center">
                      {selectedAgent.name}
                    </p>
                    <div className="flex items-center gap-1 text-xs">
                      <Icon name="StarIcon" size={12} variant="solid" className="text-yellow-400" />
                      <span className="font-body text-textSecondary">
                        {selectedAgent.rating.toFixed(1)} ({selectedAgent.reviewCount})
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-stretch gap-2">
                    <a
                      href={`https://wa.me/${selectedAgent.phone}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-accent text-secondary rounded-lg font-cta font-bold hover:bg-primary hover:text-accent transition-all text-center text-sm"
                    >
                      WhatsApp
                    </a>
                    <a
                      href={`/agent-profile/${selectedAgent.slug}`}
                      className="px-3 py-1.5 border border-accent text-accent rounded-lg font-cta font-bold hover:bg-accent/10 transition-all text-center text-sm"
                    >
                      Profil
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Stats cards - bottom left */}
            <div className="absolute bottom-6 left-6 z-30 space-y-2">
              <div className="bg-white/90 backdrop-blur rounded-xl border border-accent/20 px-4 py-3 shadow-lg">
                <div className="text-xs font-body text-textSecondary">Order Siap</div>
                <div className="text-xl font-heading font-black text-primary">
                  {completedOrdersCount.toLocaleString()}
                </div>
              </div>
              <div className="bg-white/90 backdrop-blur rounded-xl border border-accent/20 px-4 py-3 shadow-lg">
                <div className="text-xs font-body text-textSecondary">Aktif Ejen</div>
                <div className="text-xl font-heading font-black text-primary">
                  {activeAgentsCount}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Testimonials testimonials={displayTestimonials} />
      <CTASection />
      <Footer />
    </div>
  );
};

export default HomepageInteractive;
